import { z } from 'zod';

export const DesktopSemanticRoleSchema = z.enum(['detail', 'collection', 'aggregate', 'assistant']);
export type DesktopSemanticRole = z.infer<typeof DesktopSemanticRoleSchema>;

export const DesktopEntityTypeSchema = z.enum(['company', 'contact', 'resource', 'need', 'positioning']);
export type DesktopEntityType = z.infer<typeof DesktopEntityTypeSchema>;

export const DesktopEntityTargetSchema = z.object({
    kind: z.literal('entity'),
    entityType: DesktopEntityTypeSchema,
    entityId: z.string().min(1),
    tab: z.string().min(1).optional(),
});
export type DesktopEntityTarget = z.infer<typeof DesktopEntityTargetSchema>;

export const DesktopCollectionTypeSchema = z.enum([
    'companies',
    'contacts',
    'candidates',
    'consultants',
    'needs',
    'positionings',
    'missions',
    'notifications',
    'resource-dts',
]);
export type DesktopCollectionType = z.infer<typeof DesktopCollectionTypeSchema>;

export const DesktopCollectionTargetSchema = z.object({
    kind: z.literal('collection'),
    collection: DesktopCollectionTypeSchema,
});
export type DesktopCollectionTarget = z.infer<typeof DesktopCollectionTargetSchema>;

export const DesktopAggregateTypeSchema = z.enum(['dashboard', 'activities', 'kanban', 'reporting', 'staffing']);
export type DesktopAggregateType = z.infer<typeof DesktopAggregateTypeSchema>;

export const DesktopAggregateTargetSchema = z.object({
    kind: z.literal('aggregate'),
    aggregate: DesktopAggregateTypeSchema,
});
export type DesktopAggregateTarget = z.infer<typeof DesktopAggregateTargetSchema>;

export const DesktopAssistantTargetSchema = z.object({
    kind: z.literal('assistant'),
    assistant: z.literal('chatbot'),
});
export type DesktopAssistantTarget = z.infer<typeof DesktopAssistantTargetSchema>;

export const DesktopSemanticTargetSchema = z.discriminatedUnion('kind', [
    DesktopEntityTargetSchema,
    DesktopCollectionTargetSchema,
    DesktopAggregateTargetSchema,
    DesktopAssistantTargetSchema,
]);
export type DesktopSemanticTarget = z.infer<typeof DesktopSemanticTargetSchema>;

export const DesktopPanelSurfaceSchema = z.enum(['board', 'floating']);
export type DesktopPanelSurface = z.infer<typeof DesktopPanelSurfaceSchema>;

export const DesktopPanelVisibilitySchema = z.enum(['visible', 'minimized']);
export type DesktopPanelVisibility = z.infer<typeof DesktopPanelVisibilitySchema>;

export const DesktopOpenedWidgetSchema = z.object({
    contentKey: z.string().min(1),
    role: DesktopSemanticRoleSchema,
    target: DesktopSemanticTargetSchema,
    visibility: DesktopPanelVisibilitySchema,
    surface: DesktopPanelSurfaceSchema,
});
export type DesktopOpenedWidget = z.infer<typeof DesktopOpenedWidgetSchema>;

export const DesktopContextSchema = z.object({
    activeBoardId: z.string().min(1),
    activeWidget: DesktopOpenedWidgetSchema.optional(),
    openedWidgets: z.array(DesktopOpenedWidgetSchema),
});
export type DesktopContext = z.infer<typeof DesktopContextSchema>;

export const DesktopFocusPolicySchema = z.enum(['take-focus', 'preserve-focus']);
export type DesktopFocusPolicy = z.infer<typeof DesktopFocusPolicySchema>;

export const DesktopUiEffectReasonSchema = z.enum(['created', 'updated', 'requested']);
export type DesktopUiEffectReason = z.infer<typeof DesktopUiEffectReasonSchema>;

export const DesktopInvalidationTargetSchema = z.discriminatedUnion('kind', [
    DesktopEntityTargetSchema,
    DesktopCollectionTargetSchema,
    DesktopAggregateTargetSchema,
]);
export type DesktopInvalidationTarget = z.infer<typeof DesktopInvalidationTargetSchema>;

export const DesktopShowEntityEffectSchema = z.object({
    kind: z.literal('show-entity'),
    target: DesktopEntityTargetSchema,
    focusPolicy: DesktopFocusPolicySchema,
    reason: DesktopUiEffectReasonSchema,
});
export type DesktopShowEntityEffect = z.infer<typeof DesktopShowEntityEffectSchema>;

export const DesktopShowCollectionEffectSchema = z.object({
    kind: z.literal('show-collection'),
    target: DesktopCollectionTargetSchema,
    focusPolicy: DesktopFocusPolicySchema,
    reason: DesktopUiEffectReasonSchema,
});
export type DesktopShowCollectionEffect = z.infer<typeof DesktopShowCollectionEffectSchema>;

export const DesktopInvalidateEffectSchema = z.object({
    kind: z.literal('invalidate'),
    targets: z.array(DesktopInvalidationTargetSchema).min(1),
    focusPolicy: DesktopFocusPolicySchema,
    reason: DesktopUiEffectReasonSchema,
});
export type DesktopInvalidateEffect = z.infer<typeof DesktopInvalidateEffectSchema>;

export const DesktopUiEffectSchema = z.discriminatedUnion('kind', [
    DesktopShowEntityEffectSchema,
    DesktopShowCollectionEffectSchema,
    DesktopInvalidateEffectSchema,
]);
export type DesktopUiEffect = z.infer<typeof DesktopUiEffectSchema>;
